import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwSecurityUserService, DwCacheService, DwEventService, DwConfigService, DwConfigServiceToken, DwSecurityTopics } from "@devwareapps/devware-cap";
import { Observable, of } from "rxjs";
import {
    AIProcessingReferenceEntity,
    QuizEntity,
    AppMetaDataItemNames,
    AppMetaDataLookups,
    DocumentAiProcessingStatusAllItems,
    FlightSchoolGroupEntity,
    FlightSchoolGroupWithStudentCourseDetailQueryEntity,
    AppMetaData,
    StudentCourseEntity,
    PackageEntity,
    InstructorEntity,
    DocumentGroupEntity,
    DocumentItemEntity,
    DocumentPublicationEntity,
    DocumentFolderEntity,
    DocumentItemStatusAllItems,
    DocumentTypeAllItems,
    DocumentOwnerTypeAllItems,
    CourseDocumentItemEntity,
    CourseModuleDocumentItemEntity,
    CourseLessonDocumentLinkEntity,
    CourseLessonEntity,
    ReferenceStatusAllItems,
    CourseEntity
} from "../../../meta-data/app-meta-data.service";
import { map, mergeMap, tap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { AdminManagerService } from "../../admin/services/admin-manager.service";
import {
    DocumentItemAiProcessingReferenceOption,
    DocumentItemAiProcessingCourseDetails,
    DocumentItemAiProcessingCourseModule,
    DocumentItemAiProcessingCourseSummary,
    DocumentItemAiProcessingLessonLink,
    DocumentItemAiProcessingSummary
} from "../models/document-item-ai-processing.model";



@Injectable({ providedIn: 'root' })
export class DocumentsRepositoryService {
    documentsApi: string;

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) private configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private adminManagerService: AdminManagerService,
        private http: HttpClient,
    ) {
        this.documentsApi = `${configService.coreConfig.apiRoot}/aviator-online/documents`;
    }

    getDocumentsRequiringAcknowledgement(studentId: number, flightSchoolId: number): Observable<DocumentItemEntity[]> {
        const query = AppMetaData.DocumentItem.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.DocumentOwnershipTypeId, DocumentOwnerTypeAllItems.FlightSchool));
        query.addFilterAnd(f => f.Equal(a => a.RequireStudentAcknowledgement, true));
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, flightSchoolId));

        const subQuery = AppMetaData.DocumentItemAcknowledgement.CreateQueryBuilder();
        subQuery.addFilterAnd(f => f.Equal(a => a.StudentId, studentId));

        query.addPrefetch(r => r.DocumentItemAcknowledgement, subQuery.query);

        return this.dwOrmDataService.executeQuery<DocumentItemEntity>(query)
            .pipe(map(documents => {
                return documents.filter(document => {
                    const acknowledged = document.DocumentItemAcknowledgement.find(ack => ack.DocumentItemId === document.DocumentItemId);
                    return !acknowledged;
                });
            }));
    }

    loadDocumentItem(DocumentItemId: number): Observable<DocumentItemEntity> {
        const query = AppMetaData.DocumentItem.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.DocumentItemId, DocumentItemId));

        const changeLogQuery = AppMetaData.DocumentItemChangeLog.CreateQueryBuilder().addOrderByDesc(a => a.CreateDateTime).query;
        changeLogQuery.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addPrefetch(r => r.DocumentItemChangeLog, changeLogQuery);

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    loadDocumentPublication(documentPublicationId: number): Observable<DocumentPublicationEntity> {
        const query = AppMetaData.DocumentPublication.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.DocumentPublicationId, documentPublicationId));
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;
        return this.dwOrmDataService.executeQuerySingle(query);
    }

    saveDocumentItem(documentItem: DocumentItemEntity): Observable<DocumentItemEntity> {
        return AppMetaData.Queries.DocumentItemsList.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(documentItemQueryBuilder => {
                return this.dwOrmDataService.saveEntity(documentItem, true, documentItemQueryBuilder.query);
            }));
    }

    requestDocumentItemAiReprocess(documentItemId: number): Observable<DocumentItemEntity> {
        return this.loadDocumentItem(documentItemId)
            .pipe(mergeMap(documentItem => {
                documentItem.DocumentAiProcessingStatusId = DocumentAiProcessingStatusAllItems.ReprocessRequested;
                return this.saveDocumentItem(documentItem);
            }));
    }

    requestCourseDocumentItemAiReprocess(documentItemId: number, courseDocumentItemId: number, force = false): Observable<void> {
        return this.http.post<void>(
            `${this.documentsApi}/${documentItemId}/ai-processing/courses/${courseDocumentItemId}/reprocess`,
            { force }
        );
    }

    requestCourseModuleDocumentItemAiReprocess(documentItemId: number, courseModuleDocumentItemId: number, force = false): Observable<void> {
        return this.http.post<void>(
            `${this.documentsApi}/${documentItemId}/ai-processing/modules/${courseModuleDocumentItemId}/reprocess`,
            { force }
        );
    }

    restoreCourseLessonDocumentLink(documentItemId: number, courseLessonDocumentLinkId: number, replaceActive = false): Observable<void> {
        return this.http.post<void>(
            `${this.documentsApi}/${documentItemId}/ai-processing/lesson-links/${courseLessonDocumentLinkId}/restore`,
            { replaceActive }
        );
    }

    requestDocumentItemChunkAiReprocess(documentItemId: number, documentItemChunkId: number, force = false): Observable<void> {
        return this.http.post<void>(
            `${this.documentsApi}/${documentItemId}/ai-processing/chunks/${documentItemChunkId}/reprocess`,
            { force }
        );
    }

    requestDocumentItemChunkAiReprocessAll(documentItemId: number, force = true): Observable<void> {
        return this.http.post<void>(
            `${this.documentsApi}/${documentItemId}/ai-processing/chunks/reprocess`,
            { force }
        );
    }

    getDocumentItemAiProcessingSummary(documentItemId: number): Observable<DocumentItemAiProcessingSummary> {
        const query = AppMetaData.DocumentItem.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
        query.addPrefetch(r => r.DocumentAiProcessingStatus);
        query.addPrefetch(r => r.DocumentItemPage);

        const chunkQuery = AppMetaData.DocumentItemChunk.CreateQueryBuilder();
        chunkQuery.addPrefetch(r => r.AIProcessingReference);
        chunkQuery.addPrefetch(r => r.DocumentItemChunkContentType);
        chunkQuery.addPrefetch(r => r.VectorReference);
        query.addPrefetch(r => r.DocumentItemChunk, chunkQuery.query);

        const courseDocumentItemQuery = AppMetaData.CourseDocumentItem.CreateQueryBuilder();
        courseDocumentItemQuery.addFilterAnd(f => f.Equal(a => a.CourseDocumentItemStatusId, ReferenceStatusAllItems.Active));
        courseDocumentItemQuery.addPrefetch(r => r.Course);
        courseDocumentItemQuery.addPrefetch(r => r.CourseDocumentProcessingStatus);
        courseDocumentItemQuery.addPrefetch(r => r.AIProcessingReference);
        query.addPrefetch(r => r.CourseDocumentItem, courseDocumentItemQuery.query);

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuerySingle<DocumentItemEntity>(query)
            .pipe(map(documentItem => ({
                DocumentItemId: documentItem?.DocumentItemId,
                DocumentItemName: documentItem?.DocumentItemName,
                DocumentAiProcessingStatusId: documentItem?.DocumentAiProcessingStatusId,
                DocumentAiProcessingStatus: documentItem?.DocumentAiProcessingStatus?.DocumentAiProcessingStatus || documentItem?.DocumentAiProcessingStatusIdDisplay,
                DocumentAiProcessingProgressText: documentItem?.DocumentAiProcessingProgressText,
                DocumentAiProcessingErrorText: documentItem?.DocumentAiProcessingErrorText,
                DocumentAiProcessingStartDateTime: documentItem?.DocumentAiProcessingStartDateTime,
                DocumentAiProcessingCompletedDateTime: documentItem?.DocumentAiProcessingCompletedDateTime,
                DocumentAiProcessingLastUpdatedDateTime: documentItem?.DocumentAiProcessingLastUpdatedDateTime,
                DocumentAiProcessingEstimatedCompletedDateTime: documentItem?.DocumentAiProcessingEstimatedCompletedDateTime,
                DocumentAiProcessingTotalChunkCount: documentItem?.DocumentAiProcessingTotalChunkCount,
                DocumentAiProcessingProcessedChunkCount: documentItem?.DocumentAiProcessingProcessedChunkCount,
                DocumentAiProcessingFailedChunkCount: documentItem?.DocumentAiProcessingFailedChunkCount,
                DocumentAiProcessingTotalModuleLinkCount: documentItem?.DocumentAiProcessingTotalModuleLinkCount,
                DocumentAiProcessingProcessedModuleLinkCount: documentItem?.DocumentAiProcessingProcessedModuleLinkCount,
                DocumentAiProcessingFailedModuleLinkCount: documentItem?.DocumentAiProcessingFailedModuleLinkCount,
                PageCount: documentItem?.DocumentItemPage?.length || 0,
                Pages: (documentItem?.DocumentItemPage || [])
                    .slice()
                    .sort((a, b) => (a.PageNumber || 0) - (b.PageNumber || 0))
                    .map(page => ({
                        DocumentItemPageId: page.DocumentItemPageId,
                        PageNumber: page.PageNumber,
                        CharacterCount: page.CharacterCount,
                        HasText: page.HasText,
                        PageText: page.PageText
                    })),
                Chunks: (documentItem?.DocumentItemChunk || [])
                    .slice()
                    .sort((a, b) => (a.ChunkIndex || 0) - (b.ChunkIndex || 0))
                    .map(chunk => ({
                        DocumentItemChunkId: chunk.DocumentItemChunkId,
                        ChunkIndex: chunk.ChunkIndex,
                        StartPageNumber: chunk.StartPageNumber,
                        EndPageNumber: chunk.EndPageNumber,
                        CharacterCount: chunk.CharacterCount,
                        ChunkSummary: chunk.ChunkSummary,
                        Keywords: chunk.Keywords,
                        Topics: chunk.Topics,
                        ImportantTerms: chunk.ImportantTerms,
                        AIProcessingReferenceId: chunk.AIProcessingReferenceId,
                        AIProcessingStatusId: chunk.AIProcessingReference?.AIProcessingStatusId,
                        AIProcessingStatus: chunk.AIProcessingReference?.AIProcessingStatusIdDisplay,
                        HasVectorReference: !!chunk.VectorReferenceId,
                        DocumentItemChunkContentTypeId: chunk.DocumentItemChunkContentTypeId,
                        DocumentItemChunkContentType: chunk.DocumentItemChunkContentType?.DocumentItemChunkContentType
                            || chunk.DocumentItemChunkContentTypeIdDisplay,
                        ExcludeFromVectorSearch: chunk.DocumentItemChunkContentType?.ExcludeFromVectorSearch
                    })),
                Courses: (documentItem?.CourseDocumentItem || [])
                    .slice()
                    .sort((a, b) => (a.Course?.CourseName || a.CourseIdDisplay || '').localeCompare(b.Course?.CourseName || b.CourseIdDisplay || ''))
                    .map(courseDocumentItem => this.mapCourseDocumentItemSummary(courseDocumentItem))
            })));
    }

    getCourseDocumentItemAiProcessing(courseDocumentItemId: number): Observable<DocumentItemAiProcessingCourseDetails> {
        const courseDocumentItemQuery = AppMetaData.CourseDocumentItem.CreateQueryBuilder();
        courseDocumentItemQuery.addFilterAnd(f => f.Equal(a => a.CourseDocumentItemId, courseDocumentItemId));
        courseDocumentItemQuery.addFilterAnd(f => f.Equal(a => a.CourseDocumentItemStatusId, ReferenceStatusAllItems.Active));

        const lessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();
        lessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));

        const courseModuleQuery = AppMetaData.CourseModule.CreateQueryBuilder();
        courseModuleQuery.addPrefetch(r => r.CourseLesson, lessonQuery.query);

        const moduleDocumentItemQuery = AppMetaData.CourseModuleDocumentItem.CreateQueryBuilder();
        moduleDocumentItemQuery.addFilterAnd(f => f.Equal(a => a.CourseModuleDocumentItemStatusId, ReferenceStatusAllItems.Active));
        moduleDocumentItemQuery.addPrefetch(r => r.AIProcessingReferenceByAiProcessingReferenceId);
        moduleDocumentItemQuery.addPrefetch(r => r.CourseModule, courseModuleQuery.query);

        courseDocumentItemQuery.addPrefetch(r => r.CourseModuleDocumentItem, moduleDocumentItemQuery.query);
        courseDocumentItemQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuerySingle<CourseDocumentItemEntity>(courseDocumentItemQuery)
            .pipe(mergeMap(courseDocumentItem => {
                const modules = (courseDocumentItem?.CourseModuleDocumentItem || [])
                    .slice()
                    .sort((a, b) => (a.CourseModule?.ModuleOrder || 0) - (b.CourseModule?.ModuleOrder || 0));
                const courseModuleIds = modules
                    .map(module => module.CourseModuleId)
                    .filter((courseModuleId): courseModuleId is number => !!courseModuleId);

                if (!courseDocumentItem?.DocumentItemId || !courseModuleIds.length) {
                    return of({
                        CourseDocumentItemId: courseDocumentItemId,
                        Modules: modules.map(moduleDocumentItem => this.mapCourseModuleDocumentItem(moduleDocumentItem))
                    });
                }

                const lessonLinkQuery = AppMetaData.CourseLessonDocumentLink.CreateQueryBuilder();
                lessonLinkQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemId, courseDocumentItem.DocumentItemId));
                lessonLinkQuery.addFilterAnd(f => f.Equal(a => a.CourseLessonDocumentLinkStatusId, ReferenceStatusAllItems.Active));
                lessonLinkQuery.addFilterAnd(f => f.InList(a => AppMetaData.CourseLesson.Attributes.CourseModuleId, courseModuleIds));
                lessonLinkQuery.addFilterAnd(f => f.Equal(a => AppMetaData.CourseLesson.Attributes.LessonStatusId, ReferenceStatusAllItems.Active));

                const activeLessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();
                activeLessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));
                lessonLinkQuery.addPrefetch(r => r.CourseLesson, activeLessonQuery.query);

                return this.dwOrmDataService.executeQuery<CourseLessonDocumentLinkEntity>(lessonLinkQuery)
                    .pipe(map(lessonLinks => {
                        const linkedLessonCountByModuleId = new Map<number, number>();

                        (lessonLinks || []).forEach(lessonLink => {
                            const linkedModuleId = lessonLink.CourseLesson?.CourseModuleId;

                            if (!linkedModuleId) {
                                return;
                            }

                            linkedLessonCountByModuleId.set(
                                linkedModuleId,
                                (linkedLessonCountByModuleId.get(linkedModuleId) || 0) + 1
                            );
                        });

                        return {
                            CourseDocumentItemId: courseDocumentItemId,
                            Modules: modules.map(moduleDocumentItem =>
                                this.mapCourseModuleDocumentItem(
                                    moduleDocumentItem,
                                    linkedLessonCountByModuleId.get(moduleDocumentItem.CourseModuleId || -1) || 0
                                ))
                        };
                    }));
            }));
    }

    getDocumentLessonsForCourseModule(documentItemId: number, courseModuleId: number): Observable<DocumentItemAiProcessingLessonLink[]> {
        const query = AppMetaData.CourseLessonDocumentLink.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.DocumentItemId, documentItemId));
        query.addFilterAnd(f => f.Equal(a => AppMetaData.CourseLesson.Attributes.CourseModuleId, courseModuleId));
        query.addFilterAnd(f => f.InList(a => a.CourseLessonDocumentLinkStatusId,
            [ReferenceStatusAllItems.Active, ReferenceStatusAllItems.Inactive]));
        query.addFilterAnd(f => f.Equal(a => AppMetaData.CourseLesson.Attributes.LessonStatusId, ReferenceStatusAllItems.Active));

        const lessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();
        lessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));
        query.addPrefetch(r => r.CourseLesson, lessonQuery.query);
        const linkReferenceQuery = AppMetaData.CourseLessonDocumentLinkReference.CreateQueryBuilder();
        linkReferenceQuery.addFields(f => [f.CourseLessonDocumentLinkReferenceId, f.ModifiedDateTime]);
        query.addPrefetch(r => r.CourseLessonDocumentLinkReference, linkReferenceQuery.query);
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery<CourseLessonDocumentLinkEntity>(query)
            .pipe(map(lessonLinks => {
                const activeLinkByLessonId = new Map<number, CourseLessonDocumentLinkEntity>();
                lessonLinks.filter(link => link.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Active)
                    .forEach(link => {
                        if (link.CourseLessonId) { activeLinkByLessonId.set(link.CourseLessonId, link); }
                    });
                const mapLessonLink = (lessonLink: CourseLessonDocumentLinkEntity): DocumentItemAiProcessingLessonLink => ({
                    CourseLessonDocumentLinkId: lessonLink.CourseLessonDocumentLinkId,
                    CourseLessonId: lessonLink.CourseLessonId,
                    AIProcessingReferenceId: lessonLink.CourseLesson?.AIProcessingReferenceId,
                    LessonName: lessonLink.CourseLesson?.LessonName || lessonLink.CourseLessonIdDisplay,
                    LessonOrder: lessonLink.CourseLesson?.LessonOrder,
                    LessonTypeIdDisplay: lessonLink.CourseLesson?.LessonTypeIdDisplay,
                    PageNumbers: lessonLink.PageNumbers,
                    FirstPageNumber: lessonLink.FirstPageNumber,
                    AIConfidence: lessonLink.AIConfidence,
                    AIMatchReason: lessonLink.AIMatchReason,
                    ModifiedDateTime: (lessonLink.CourseLessonDocumentLinkReference || [])
                        .map(reference => reference.ModifiedDateTime)
                        .filter((value): value is string => !!value)
                        .sort()
                        .pop(),
                    IsActive: lessonLink.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Active
                });

                return lessonLinks
                    .filter(link => link.CourseLessonDocumentLinkStatusId === ReferenceStatusAllItems.Active
                        || !!link.PageNumbers || !!link.FirstPageNumber)
                    .slice()
                    .sort((a, b) => (a.CourseLesson?.LessonOrder || 0) - (b.CourseLesson?.LessonOrder || 0))
                    .map(link => {
                        const mapped = mapLessonLink(link);
                        const activeLink = link.CourseLessonId ? activeLinkByLessonId.get(link.CourseLessonId) : undefined;
                        if (!mapped.IsActive && activeLink) { mapped.ActiveLink = mapLessonLink(activeLink); }
                        return mapped;
                    });
            }));
    }

    getAiProcessingReferenceOptionsForCourseModule(module: DocumentItemAiProcessingCourseModule): Observable<DocumentItemAiProcessingReferenceOption[]> {
        const courseModuleId = module.CourseModuleId;

        if (!courseModuleId) {
            return of([]);
        }

        const aiReferenceQuery = AppMetaData.AIProcessingReference.CreateQueryBuilder();
        aiReferenceQuery.addFilterAnd(f => f.Equal(a => a.MetaDataItemId, AppMetaDataLookups.CourseModuleAllItems));
        aiReferenceQuery.addFilterAnd(f => f.Equal(a => a.KeyValue, courseModuleId));
        aiReferenceQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery<AIProcessingReferenceEntity>(aiReferenceQuery)
            .pipe(map(referenceItems => {
                const normalizedReferenceItems = (referenceItems || [])
                    .filter(referenceItem => !!referenceItem.AIProcessingReferenceId)
                    .sort((a, b) => (b.ModifiedDateTime || '').localeCompare(a.ModifiedDateTime || ''));

                if (!normalizedReferenceItems.length && module.AiProcessingReferenceId) {
                    return [{
                        AIProcessingReferenceId: module.AiProcessingReferenceId,
                        Label: `${module.CourseModuleName || 'Module'} module linking`,
                        Type: 'module',
                        SortOrder: 0
                    }];
                }

                return normalizedReferenceItems.map((referenceItem, index) => ({
                    AIProcessingReferenceId: referenceItem.AIProcessingReferenceId!,
                    Label: normalizedReferenceItems.length > 1
                        ? `${module.CourseModuleName || 'Module'} module linking ${index + 1}`
                        : `${module.CourseModuleName || 'Module'} module linking`,
                    Type: 'module' as const,
                    SortOrder: index
                }));
            }));
    }

    getActiveCourseDocuments(courseId: number): Observable<DocumentItemEntity[]> {
        const query = AppMetaData.CourseDocumentItem.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
        query.addFilterAnd(f => f.Equal(a => a.CourseDocumentItemStatusId, ReferenceStatusAllItems.Active));

        const documentItemQuery = AppMetaData.DocumentItem.CreateQueryBuilder();
        documentItemQuery.addFilterAnd(f => f.Equal(a => a.DocumentItemStatusId, DocumentItemStatusAllItems.Active));
        documentItemQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addPrefetch(r => r.DocumentItem, documentItemQuery.query);
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery<CourseDocumentItemEntity>(query)
            .pipe(map(courseDocumentItems => (courseDocumentItems || [])
                .map(courseDocumentItem => courseDocumentItem.DocumentItem)
                .filter(documentItem => !!documentItem?.DocumentItemId)
                .filter(documentItem => documentItem.DocumentTypeId !== DocumentTypeAllItems.SketchFab3D)
                .map(documentItem => ({
                    ...documentItem,
                    DocumentItemName: documentItem.DocumentItemName || `Document ${documentItem.DocumentItemId}`
                }))
                .sort((a, b) => (a.DocumentItemName || '').localeCompare(b.DocumentItemName || ''))));
    }

    getActiveCourses(): Observable<CourseEntity[]> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.CourseStatusId, ReferenceStatusAllItems.Active));
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery<CourseEntity>(query)
            .pipe(map(courses => (courses || [])
                .filter(course => !!course.CourseId)
                .map(course => ({
                    ...course,
                    CourseName: course.CourseName || `Course ${course.CourseId}`
                }))
                .sort((a, b) => (a.CourseName || '').localeCompare(b.CourseName || ''))));
    }

    loadDocumentFolder(DocumentFolderId: number): Observable<DocumentFolderEntity> {
        const query = AppMetaData.DocumentFolder.CreateQueryBuilder();
        query.addFilterAnd(f => f.Equal(a => a.DocumentFolderId, DocumentFolderId));

        const changeLogQuery = AppMetaData.DocumentFolderChangeLog.CreateQueryBuilder().addOrderByDesc(a => a.CreateDateTime).query;
        changeLogQuery.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addPrefetch(r => r.DocumentFolderChangeLog, changeLogQuery);

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    saveDocumentFolder(documentFolder: DocumentFolderEntity): Observable<DocumentFolderEntity> {
        return this.dwOrmDataService.saveEntity(documentFolder, true);
    }

    getRootFolderName(): string {
        return 'Main Folder';
    }

    getDocumentFoldersFromTo(documentGroupid: number, oldFolderId: number, newFolderId: number): Observable<string> {

        const rootFolderName = this.getRootFolderName();
        return this.getDocumentGroup(documentGroupid).pipe(
            map(documentGroup => {
                const oldFolder = documentGroup.DocumentFolder.find(folder => folder.DocumentFolderId === oldFolderId);
                const newFolder = documentGroup.DocumentFolder.find(folder => folder.DocumentFolderId === newFolderId);

                return `${oldFolder ? oldFolder.FolderName : rootFolderName} to ${newFolder ? newFolder.FolderName : rootFolderName}`;
            }));
    }

    getDocumentGroup(documentGroupId: number): Observable<DocumentGroupEntity> {
        const cacheInvalidationTopics: string[] = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DocumentFolder.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DocumentItem.ItemDetail.itemName));

        const key = `DocumentGroup_${documentGroupId}`;

        // The installed CAP cache implementation takes this value in minutes.
        return this.dwCacheService.get(key, this.loadDocumentGroup(documentGroupId), 10, cacheInvalidationTopics);
    }

    private loadDocumentGroup(documentGroupId: number): Observable<DocumentGroupEntity> {
        // Note: Security is handled on the server side

        const aviatorUser = this.adminManagerService.getAviatorUser();

        return AppMetaData.Queries.DocumentItemsList.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(documentItemQueryBuilder => {
                const query = AppMetaData.DocumentGroup.CreateQueryBuilder();

                query.addFilterAnd(f => f.Equal(a => a.DocumentGroupId, documentGroupId));

                documentItemQueryBuilder.addFilterAnd(fr =>
                    fr.GroupAnd(f =>
                        f.GroupOr(f2 =>
                            f2.GroupAnd(
                                f3 => f3.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId),
                                f3 => f3.Equal(a => a.DocumentOwnershipTypeId, DocumentOwnerTypeAllItems.FlightSchool
                                )),
                            f4 => f4.Equal(a => a.DocumentOwnershipTypeId, DocumentOwnerTypeAllItems.AviatorPro)
                        )));

                const folderQuery = AppMetaData.DocumentFolder.CreateQueryBuilder();

                folderQuery.addFilterAnd(fr =>
                    fr.GroupAnd(f =>
                        f.GroupOr(f2 =>
                            f2.GroupAnd(
                                f3 => f3.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId),
                                f3 => f3.Equal(a => a.DocumentOwnershipTypeId, DocumentOwnerTypeAllItems.FlightSchool
                                )),
                            f4 => f4.Equal(a => a.DocumentOwnershipTypeId, DocumentOwnerTypeAllItems.AviatorPro)
                        )));


                query.addPrefetch(f => f.DocumentFolder, folderQuery.query);
                query.addPrefetch(f => f.DocumentItem, documentItemQueryBuilder.query);

                return this.dwOrmDataService.executeQuerySingle(query);
            }));
    }

    private mapCourseDocumentItemSummary(courseDocumentItem: CourseDocumentItemEntity): DocumentItemAiProcessingCourseSummary {
        return {
            CourseDocumentItemId: courseDocumentItem.CourseDocumentItemId,
            CourseId: courseDocumentItem.CourseId,
            CourseName: courseDocumentItem.Course?.CourseName || courseDocumentItem.CourseIdDisplay,
            CourseDocumentProcessingStatusId: courseDocumentItem.CourseDocumentProcessingStatusId,
            CourseDocumentProcessingStatus: courseDocumentItem.CourseDocumentProcessingStatus?.CourseDocumentProcessingStatus || courseDocumentItem.CourseDocumentProcessingStatusIdDisplay,
            CourseDocumentProcessingProgressText: courseDocumentItem.CourseDocumentProcessingProgressText,
            CourseDocumentProcessingErrorText: courseDocumentItem.CourseDocumentProcessingErrorText,
            CourseDocumentProcessingStartDateTime: courseDocumentItem.CourseDocumentProcessingStartDateTime,
            CourseDocumentProcessingCompletedDateTime: courseDocumentItem.CourseDocumentProcessingCompletedDateTime,
            CourseDocumentProcessingLastUpdatedDateTime: courseDocumentItem.CourseDocumentProcessingLastUpdatedDateTime,
            CourseDocumentProcessingEstimatedCompletedDateTime: courseDocumentItem.CourseDocumentProcessingEstimatedCompletedDateTime,
            CourseDocumentProcessingTotalModuleCount: courseDocumentItem.CourseDocumentProcessingTotalModuleCount,
            CourseDocumentProcessingProcessedModuleCount: courseDocumentItem.CourseDocumentProcessingProcessedModuleCount,
            CourseDocumentProcessingFailedModuleCount: courseDocumentItem.CourseDocumentProcessingFailedModuleCount,
            AIProcessingReferenceId: courseDocumentItem.AIProcessingReferenceId,
            AIProcessingReferenceStatusId: courseDocumentItem.AIProcessingReference?.AIProcessingStatusId,
            AIProcessingReferenceStatus: courseDocumentItem.AIProcessingReference?.AIProcessingStatusIdDisplay
        };
    }

    private mapCourseModuleDocumentItem(
        moduleDocumentItem: CourseModuleDocumentItemEntity,
        linkedLessonCount: number = 0
    ): DocumentItemAiProcessingCourseModule {
        return {
            CourseModuleDocumentItemId: moduleDocumentItem.CourseModuleDocumentItemId,
            CourseModuleId: moduleDocumentItem.CourseModuleId,
            CourseModuleName: moduleDocumentItem.CourseModule?.ModuleName || moduleDocumentItem.CourseModuleIdDisplay,
            ModuleOrder: moduleDocumentItem.CourseModule?.ModuleOrder,
            AiProcessingReferenceId: moduleDocumentItem.AiProcessingReferenceId,
            AIProcessingStatusId: moduleDocumentItem.AiProcessingReferenceIdAIProcessingReference?.AIProcessingStatusId,
            AIProcessingStatus: moduleDocumentItem.AiProcessingReferenceIdAIProcessingReference?.AIProcessingStatusIdDisplay || moduleDocumentItem.AiProcessingReferenceIdDisplay,
            TotalLessonCount: (moduleDocumentItem.CourseModule?.CourseLesson || []).length,
            LinkedLessonCount: linkedLessonCount
        };
    }

}
